import { apiService } from './api';

// ─── Report DTOs ───────────────────────────────────────────────────────────

export interface SalesSummaryDTO {
  totalSales: number;
  totalInvoices: number;
  paidAmount: number;
  unpaidAmount: number;
  paidInvoices: number;
  unpaidInvoices: number;
  partiallyPaidInvoices: number;
  averageInvoiceValue: number;
  periodStart: string;
  periodEnd: string;
}

export interface SalesByProductDTO {
  productId: number;
  productName: string;
  productSKU: string;
  totalQuantity: number;
  totalRevenue: number;
  numberOfInvoices: number;
  averagePrice: number;
}

export interface SalesByCustomerDTO {
  customerId: number;
  customerName: string;
  customerCode: string;
  totalSales: number;
  numberOfInvoices: number;
  amountPaid: number;
  amountOutstanding: number;
  averageInvoiceValue: number;
  lastSaleDate: string;
  // Restaurant-order share already folded into totalSales/amountPaid/amountOutstanding
  // above, matched by customer name. customerId === 0 is the walk-in bucket.
  numberOfRestaurantOrders: number;
  restaurantRevenue: number;
}

export interface SalesTrendDTO {
  date: string;
  totalSales: number;
  numberOfInvoices: number;
  paidAmount: number;
  unpaidAmount: number;
  // Restaurant-order share already folded into totalSales/paidAmount/unpaidAmount above.
  restaurantRevenue: number;
  numberOfRestaurantOrders: number;
}

export interface SalesInvoiceStatusDTO {
  status: string;
  count: number;
  totalAmount: number;
  percentage: number;
}

export interface TopProductDTO {
  productId: number;
  productName: string;
  totalQuantity: number;
  totalRevenue: number;
}

export interface TopCustomerDTO {
  customerId: number;
  customerName: string;
  totalSales: number;
  numberOfInvoices: number;
}

// ─── Sales Dashboard — combined invoice + restaurant-order KPIs ───────────────

export interface SalesDashboardTrendPointDTO {
  month: string; // 'YYYY-MM'
  invoiceRevenue: number;
  restaurantRevenue: number;
}

export interface SalesDashboardRecentTransactionDTO {
  source: 'Invoice' | 'RestaurantOrder';
  id: number;
  reference: string;
  customerName: string | null;
  date: string;
  status: string;
  totalAmount: number;
  balanceDue: number;
}

export interface SalesDashboardSummaryDTO {
  invoiceRevenue: number;
  invoiceCount: number;
  restaurantRevenue: number;
  restaurantOrderCount: number;
  combinedRevenue: number;
  combinedTransactionCount: number;
  monthlyTrend: SalesDashboardTrendPointDTO[];
  recentTransactions: SalesDashboardRecentTransactionDTO[];
}

// ─── Request parameters ────────────────────────────────────────────────────

export interface ReportDateRangeParams {
  startDate?: string;
  endDate?: string;
}

export interface SalesReportParams extends ReportDateRangeParams {
  customerId?: number;
  productId?: number;
  status?: string;
  limit?: number;
}

// ─── Report service ────────────────────────────────────────────────────────

const SALES_REPORTS_API = '/reports/sales';

export const salesReportService = {
  // Summary report
  getSalesSummary: (params?: ReportDateRangeParams) =>
    apiService.get<SalesSummaryDTO>(`${SALES_REPORTS_API}/summary`, params || {}),

  // Sales by product
  getSalesByProduct: (params?: SalesReportParams) =>
    apiService.get<SalesByProductDTO[]>(`${SALES_REPORTS_API}/by-product`, params || {}),

  // Sales by customer
  getSalesByCustomer: (params?: SalesReportParams) =>
    apiService.get<SalesByCustomerDTO[]>(`${SALES_REPORTS_API}/by-customer`, params || {}),

  // Sales trends over time
  getSalesTrends: (params?: SalesReportParams & { groupBy?: 'day' | 'week' | 'month' }) =>
    apiService.get<SalesTrendDTO[]>(`${SALES_REPORTS_API}/trends`, params || {}),

  // Invoice status distribution
  getInvoiceStatusDistribution: (params?: ReportDateRangeParams) =>
    apiService.get<SalesInvoiceStatusDTO[]>(`${SALES_REPORTS_API}/status-distribution`, params || {}),

  // Top products
  getTopProducts: (params?: SalesReportParams & { limit?: number }) =>
    apiService.get<TopProductDTO[]>(`${SALES_REPORTS_API}/top-products`, params || {}),

  // Top customers
  getTopCustomers: (params?: SalesReportParams & { limit?: number }) =>
    apiService.get<TopCustomerDTO[]>(`${SALES_REPORTS_API}/top-customers`, params || {}),

  // Dashboard — combined invoice + restaurant-order KPIs
  getDashboard: () =>
    apiService.get<SalesDashboardSummaryDTO>(`${SALES_REPORTS_API}/dashboard`),
};
